import { create } from "zustand";
import type { LockStatus } from "../types/lock";
import { uuid } from "../types";

interface IncomingRequest {
  /** Email of the requester. */
  email: string;
  /** Wall-clock ms when the request arrived — countdown timer ticks against this. */
  since: number;
}

interface LockState {
  /** UUID generated once per tab. Sent on every authenticated lock call. */
  sessionId: string;
  /** UI state machine — see types/lock.ts for transitions. */
  lockStatus: LockStatus;
  /** Email of the user holding the lock (when not us). */
  lockedBy: string | null;
  /** When set, the lock holder sees the access-request banner. */
  incomingRequest: IncomingRequest | null;
  /** Outcome of the caller's own outgoing access request. */
  outgoingRequestStatus: "Pending" | "Granted" | "Denied" | null;
  /** Seconds remaining on the active countdown (request expiry or auto-grant timer). */
  countdownSeconds: number | null;
  /** When another user is already requesting, holders/observers see the Request button disabled with their email. */
  requestInProgress: string | null;
  /** True while the SignalR WebSocket is connected to the hub. */
  signalRConnected: boolean;

  setLockStatus: (status: LockStatus, lockedBy?: string | null) => void;
  setIncomingRequest: (req: IncomingRequest | null) => void;
  setOutgoingStatus: (status: LockState["outgoingRequestStatus"]) => void;
  setCountdown: (seconds: number | null) => void;
  setRequestInProgress: (email: string | null) => void;
  setSignalRConnected: (connected: boolean) => void;
  /** Clear transient lock state on unmount / form change. Keeps sessionId — it's the tab identity. */
  resetTransient: () => void;
}

const SESSION_STORAGE_KEY = "optimyl.formLock.sessionId";

function getOrCreateSessionId(): string {
  // sessionStorage is per-tab and survives hard reload, so the session
  // identity stays stable for the lifetime of the tab. Without persistence
  // the user reloading their own form sees themselves locked out by the
  // 90s Redis TTL on their previous (now-orphaned) session.
  try {
    const existing = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) return existing;
    // uuid() falls back when crypto.randomUUID is missing: that API exists only in secure contexts, and a
    // phone testing over plain http (BrowserStack's bs-local.com, a LAN address) is not one. An unguarded
    // call here ran at module load and blanked the whole app on such hosts.
    const fresh = uuid();
    sessionStorage.setItem(SESSION_STORAGE_KEY, fresh);
    return fresh;
  } catch {
    // sessionStorage can throw (private mode quotas, disabled storage).
    // Falling back to a per-load UUID is degraded but functional.
    return uuid();
  }
}

export const useLockStore = create<LockState>()((set) => ({
  sessionId: getOrCreateSessionId(),
  lockStatus: "loading",
  lockedBy: null,
  incomingRequest: null,
  outgoingRequestStatus: null,
  countdownSeconds: null,
  requestInProgress: null,
  signalRConnected: false,

  setLockStatus: (lockStatus, lockedBy) => set({ lockStatus, lockedBy: lockedBy ?? null }),
  setIncomingRequest: (incomingRequest) => set({ incomingRequest }),
  setOutgoingStatus: (outgoingRequestStatus) => set({ outgoingRequestStatus }),
  setCountdown: (countdownSeconds) => set({ countdownSeconds }),
  setRequestInProgress: (email) => set({ requestInProgress: email }),
  setSignalRConnected: (signalRConnected) => set({ signalRConnected }),

  resetTransient: () => set({
    lockStatus: "loading",
    lockedBy: null,
    incomingRequest: null,
    outgoingRequestStatus: null,
    countdownSeconds: null,
    requestInProgress: null,
  }),
}));
